import * as d3 from 'd3';
import * as _ from 'lodash';

import { ColoringCondition } from './ColoringCondition';
import { TimeSeriesBase } from './TimeSeriesBase';
import { ITimeSeries } from './ITimeSeries';
import { SymbolScaleHelper, ILogger, LoggerFactory, wideStepAfterInterpolator } from '../../../helpers/helpers';
import { getOrdinalScale } from './getOrdinalScale';
import { RectFormat } from '../../charts/interfaces';
import { LineChartData } from '../../../models/LineChartData';
import { renderLabel } from '../stack/renderLabels';
import { filterLabels } from './filterLabels';
import { renderSymbol } from '../stack/renderSymbols';

export interface ITimeStamp {
    timeStamp: string;
}

export class LineSeries extends TimeSeriesBase implements ITimeSeries {
    value: { (d: any, i?: number): number };
    name: string;

    private _color: string;
    private _coloringConditions: ColoringCondition[];
    private _count = 1;
    private _crosshairFormat: (d: any) => string;
    private _data: any[];
    private _format = d3.format('.2f');
    private _group: d3.Selection<any>;
    private _hoverHighlightedSerie: boolean;
    private _identifier: any;
    private _index = 0;
    private _interpolate: string;
    private _isBarsVisible: boolean;
    private _isLineVisible: boolean;
    private _isStacked: boolean;
    private _isSymbolVisible: boolean;
    private _isValueLabelsVisible: boolean;
    private _isXAxis?: boolean;
    private _legendAttributes: any;
    private _legendText: string;
    private _line: d3.svg.Line<any>;
    private _lineStyle?: string;
    private _logger: ILogger;
    private _previousLineStyle: string;
    private _selectionFunction: any;
    private _symbol: d3.svg.Symbol<any>;
    private _symbolScale = 0.5;
    private _xScale: d3.time.Scale<any, any>;
    private _yScale: d3.scale.Linear<any, any>;

    constructor(container: d3.Selection<any>,
        name: string,
        color: string,
        value: { (data: any, index?: number): any },
        identifier,
        selector
    ) {
        super();
        this._logger = LoggerFactory.getLogger('WideTech.Charting.lineSeries', false, 2);
        this.value = value;
        this.name = name;
        this._legendText = this.name;
        this._coloringConditions = [];

        this.interpolate('linear');
        this.color(color);
        this.isLineVisible(true);
        this.isSymbolVisible(false);
        this.identifier(identifier);
        this.selector(selector);
        this.init(container, color);
        this._group.attr('id', `seriesId${identifier}`);
    }

    private init(container: d3.Selection<any>, color: string) {
        this._logger.debug('Initializing series.');
        this._group = super.initBase(container);
        this._group.append('g')
            .classed('bars', true);
        this._group.append('path')
            .classed('line', true)
            .classed('trend', true)
            .style({
                stroke: color
            });
        this._group.append('g')
            .classed('symbols', true);
        this._group.append('g')
            .classed('value-labels', true);
        this._line = d3.svg.line<RectFormat<LineChartData>>()
            .x(d => d.x)
            .y(d => d.y);
        this._logger.debug('Series initialized.');
    }

    private drawLine(formatted: RectFormat<LineChartData>[], lineStyle: string) {
        try {
            const selection = this._group.selectAll('.line');
            if (lineStyle) {
                if (this._previousLineStyle) {
                    selection.classed(this._previousLineStyle, false);
                }
                selection.classed(lineStyle, true);
                this._previousLineStyle = lineStyle;
            }
            if (this.isLineVisible()) {
                let interpolate: any = this.interpolate();
                const wideStep = (interpolate === 'wide-step');
                if (wideStep) {
                    interpolate = wideStepAfterInterpolator;
                }
                this._line
                    .defined(d => (d && d.item.value !== null) || wideStep)
                    .interpolate(interpolate);
                const path = this._line(formatted);
                selection.attr('d', path);
            } else {
                selection.attr('d', '');
            }
        } catch (e) {
            this._logger.error(e);
        }
    }

    private drawSymbols(currentData: RectFormat<LineChartData>[]) {
        try {
            if (this._xScale && this._yScale) {
                this._logger.debug('Drawing symbols.');
                if (this.isSymbolVisible()) {
                    const tmp = this._group
                        .select('.symbols')
                        .selectAll('.scatter-series')
                        .data(currentData.filter(d => d.item.value !== null));
                    renderSymbol(tmp, this._symbolScale * (this._ratio || 1));

                    if (this._highlights && this._highlights.length > 0) {
                        this._highlights.forEach(h => this.setHighlight(h));
                    }
                } else {
                    this._group
                        .selectAll('.scatter-series')
                        .remove();
                }
            }
            this._logger.debug('Symbols drawn.');
        } catch (e) {
            this._logger.error(e);
        }
    }

    private drawBars(currentData: RectFormat<LineChartData>[]): void {
        try {
            if (this._xScale && this._yScale) {
                this._logger.debug('Drawing bars.');
                if (this.isBarsVisible()) {
                    const selection = this._group
                        .select('.bars')
                        .selectAll('.bar')
                        .data(currentData);
                    selection.exit()
                        .remove();
                    const enterSelection = selection.enter();
                    enterSelection
                        .append('g')
                        .classed('bar', true)
                        .append('rect');
                    const zero = this._yScale(0);
                    selection
                        .select('rect')
                        .attr('x', d => d.x - d.width / 2 + d.offsetX)
                        .attr('y', d => d.item.value < 0 ? zero : zero - d.height)
                        .attr('width', d => d.width)
                        .attr('height', d => d.height)
                        .style('fill', d => d.item.color)
                        .style('stroke', d => d.item.color);

                    this._logger.debug('Bars drawn.');
                } else {
                    this._group.selectAll('.bar')
                        .remove();
                }
            }
        } catch (e) {
            this._logger.error(e);
        }
    }

    private drawValueLabels(formatted: RectFormat<LineChartData>[]) {
        try {
            if (this.isValueLabelsVisible()) {
                const ratio = this._ratio || 1;
                this._logger.debug('Drawing value labels.');
                const filtered = filterLabels(formatted, this._xScale, ratio);
                const selection = this._group.select('.value-labels')
                    .selectAll('.value-label')
                    .data(filtered);
                renderLabel(selection, false, ratio);
                this._logger.debug('Value labels drawn.');
            } else {
                this._group.select('.value-labels')
                    .selectAll('.value-label')
                    .remove();
            }
        } catch (e) {
            this._logger.error(e);
        }
    }

    private draw(data: any[]) {
        data = this.isStacked() ? [] : data || [];
        let offset = 0;
        let width = 0;
        const zero = this._yScale(0);

        if (this.isBarsVisible() && this._xScale) {
            const ordinalScale = getOrdinalScale(data.map(d => d.timeStamp),
                this._xScale.domain() as [Date, Date],
                this._xScale.range() as [number, number]);
            width = ordinalScale.rangeBand() / this._count;
            offset = (this._index * width);
        }
        const color = this.getColor();
        const fmt = this.crosshairFormat();
        const formatted: RectFormat<LineChartData>[] = data.map((d, i) => {
            const value = this.value(d);
            const date = new Date(d.timeStamp);
            const x = this._xScale(date);
            const y = this._yScale(value);
            return {
                x,
                offsetX: offset,
                y,
                width: width,
                height: Math.abs(zero - y),
                color: color(d, i),
                timeStamp: d.timeStamp,
                item: {
                    value,
                    formattedValue: (value === null || value === undefined)
                        ? ''
                        : fmt ? fmt(d) : this._format(value),
                    label: '',
                    symbol: this.symbol(),
                    color: color(d, i),
                    timeStamp: date
                }
            };
        });

        this.drawBars(formatted);
        this.drawLine(formatted, this.lineStyle());
        this.drawSymbols(formatted);
        this.drawValueLabels(formatted);
    }

    private getColor(): (d, i) => string {
        const self = this;
        return (d, _i) => {
            let color = self.color();
            if (self._coloringConditions && self._coloringConditions.length > 0) {
                const match = self._coloringConditions.filter(c => c.condition(d));
                if (match && match.length > 0) {
                    color = match[0].color;
                }
            }
            return color;
        };
    }

    update(data: any, xScale, yScale): void {
        this.data(data);
        this._xScale = xScale;
        this._yScale = yScale;
        if (xScale && yScale) {
            this._symbolScale = SymbolScaleHelper.scale(this._xScale, this._yScale);
        }
        if (data) {
            this.draw(data);
        }
    }

    updateScales(xScale, yScale) {
        this._xScale = xScale;
        this._yScale = yScale;
        const currentData = this._data;
        this.draw(currentData);

    }

    translate(translateX: number, translateY: number) {
        // Nothing to do
    }

    legendText(text?: string): string {
        if (arguments.length) {
            this._legendText = text;
        }
        return this._legendText;
    }

    data(value?: any[]): any[] {
        if (arguments.length && value !== this._data) {
            this._data = value;
            this._data.forEach(d => d.date = new Date(d.timeStamp));
        }
        return this._data;
    }

    changeXScale(xScale) {
        this.changeScale(xScale, this._yScale);
    }

    changeScale(xScale, yScale) {
        this.update(this._data, xScale, yScale);
    }

    resize(xScale, yScale) {
        if (this._xScale) {
            this._xScale.range(xScale.range());
        } else {
            this._xScale = xScale;
        }
        if (this._yScale) {
            this._yScale.range(yScale.range());
        } else {
            this._yScale = yScale;
        }
        this.changeScale(this._xScale, this._yScale);
    }

    selector(selectionFunction?): { (d): any } {
        if (arguments.length) {
            this._selectionFunction = selectionFunction;
        }
        return this._selectionFunction;
    }

    private _ratio: number = 1;
    ratio(): number;
    ratio(value: number): this;
    ratio(value?: number) {
        if (arguments.length) {
            this._ratio = value;
            return this;
        }
        return this._ratio;
    }

    identifier(value?: any): any {
        if (arguments.length) {
            this._identifier = value;
        }
        return this._identifier;
    }

    hasMin(): boolean {
        return this.hasOwnAxis() && !!this.yMin() || this.yMin() === 0;
    }

    hasMax(): boolean {
        return this.hasOwnAxis() && !!this.yMax() || this.yMax() === 0;
    }

    min(): number {
        const self = this;
        let min = null;
        if (this.hasMin()) {
            min = this.yMin();
        } else {
            if (self._data) {
                min = (d3.min(self._data, d => self.value(d)) as number);
            }
        }
        return min;
    }

    max(): number {
        const self = this;
        let max = null;
        if (this.hasMax()) {
            max = this.yMax();
        } else {
            if (self._data) {
                max = (d3.max(self._data, d => self.value(d)) as number);
            }
        }
        return max;
    }

    remove(): void {
        this._group.remove();
    }

    color(value?: string): string {
        if (arguments.length) {
            this._color = value;
            if (this._group) {
                this._group.selectAll('.line')
                    .style('stroke', this._color);
                this._group.selectAll('.scatter-series')
                    .style('fill', this._color);
            }
        }
        return this._color;
    }

    crosshairLabel(bisect, date: Date, valueFormatter) {
        let label = '';
        let currentValue;
        const data = this.data();
        if (data) {
            currentValue = this.getCurrentValue(date, data, bisect);
        }
        label = this.legendText() + ': ';
        if (currentValue) {
            const value = this.value(currentValue);
            let formatted: number | string;
            try {
                const crosshairFormat = this.crosshairFormat();
                if (crosshairFormat) {
                    formatted = crosshairFormat(currentValue);
                } else {
                    formatted = valueFormatter(value);
                }
            } catch (e) {
                formatted = value;
            }
            if (value === null) {
                formatted = 'No data';
            } else if (formatted === 'NaN' || value === null || value === undefined) {
                formatted = value;
            }
            label += formatted;
        } else {
            label += 'No data';
        }
        return label;
    }

    private getCurrentValue(date: Date, data: any[], bisect: any) {
        let currentValue;
        try {
            const time = date.getTime();
            currentValue = _.find(data, d => d.date.getTime() === time);
            // Commented due to #13249
            //currentValue = data[bisect(data, date)];
            if (!currentValue) {
                // Must remove 1 due to the way bisect works and how we use it
                const index = bisect(data, date) - 1;
                if (index >= 0 && index < data.length) {
                    currentValue = data[index];
                }
            }
        } catch (e) {
            this._logger.error(e);
        }
        return currentValue;
    }

    symbol(value?: d3.svg.Symbol<any>): d3.svg.Symbol<any> {
        if (arguments.length) {
            this._symbol = value;
            //TODO update chart if symbol is visible
            if (this.isSymbolVisible()) {
                this.redraw();
            }
        }
        return this._symbol;
    }

    isSymbolVisible(value?: boolean): boolean {
        if (arguments.length) {
            this._isSymbolVisible = value;
            //TODO update chart
            this.redraw();
        }
        return this._isSymbolVisible;
    }

    isLineVisible(isVisible?: boolean): boolean {
        if (arguments.length) {
            this._isLineVisible = isVisible;
            //TODO update chart
            this.redraw();
        }
        return this._isLineVisible;
    }

    lineStyle(lineStyle?: string): string {
        if (arguments.length) {
            this._lineStyle = lineStyle;
        }
        return this._lineStyle;
    }

    isBarsVisible(isVisible?: boolean): boolean {
        if (arguments.length) {
            this._isBarsVisible = isVisible;
            //TODO update chart
            this.redraw();
        }
        return this._isBarsVisible;
    }

    isValueLabelsVisible(value?: boolean): boolean {
        if (arguments.length) {
            this._isValueLabelsVisible = value;
            //TODO update chart
            this.redraw();
        }
        return this._isValueLabelsVisible;
    }

    interpolate(value?: string): string {
        if (arguments.length) {
            this._interpolate = value;
        }
        return this._interpolate;
    }

    coloringConditions(value?: ColoringCondition[]): ColoringCondition[] {
        if (arguments.length) {
            this._coloringConditions = value;
        }
        return this._coloringConditions;
    }

    classed(value: string, isClassed?: boolean): boolean {
        if (isClassed !== null && isClassed !== undefined) {
            this._group.classed(value, isClassed);
        }
        return this._group.classed(value);
    }

    isStacked(value?: boolean): boolean {
        if (arguments.length) {
            this._isStacked = value;
        }
        return this._isStacked;
    }

    isXAxis(value?: boolean): boolean {
        if (arguments.length) {
            this._isXAxis = value;
        }
        return this._isXAxis;
    }

    updateBars(index: number, count: number) {
        this._index = index;
        this._count = count;
    }

    highlight(values: any[], keyFunction: (d: any) => any, classed: string, secondaryClass: string) {
        const id = classed + secondaryClass;
        let highlight = _.find(this._highlights, h => h.id === id);
        if (values && values.length > 0 && secondaryClass !== this.name) {
            if (highlight) {
                highlight.values = values;
            } else {
                highlight = {
                    classes: [classed, secondaryClass],
                    id: id,
                    values: values,
                    keyFunction: keyFunction
                };
                this._highlights.push(highlight);
            }
        } else {
            if (highlight) {
                highlight.values = [];
            }
        }
        if (highlight) {
            this.setHighlight(highlight);
        }
    }

    getSerieState(): number {
        return this._serieState;
    }

    setSerieState(goNextState: boolean, state?: number, reset?: boolean) {
        if (this._serieState === undefined || this._serieState === null) {
            this._serieState = 0;
        }

        // When serieState is not defined
        if (state !== undefined && state !== null) {
            this.updateSerieState(state);
        }

        // When user click on legend to go to next state
        if (goNextState) {
            this.updateSerieState();
        }
        // Highlighted
        if ((this._data && this._data.length > 0 && this._serieState === 2)
            || (!goNextState && this._serieState === 0 && !reset)) {
            this.isVisible(true);
            this.setHighlightClass(true);
            // Hidden
        } else if (this._serieState === 1) {
            this.isVisible(false);
            this.setHighlightClass(false);
            // Visible
        } else if (this._serieState === 0 || reset) {
            this.isVisible(true);
            this.setHighlightClass(false);
        }
        this.redraw();
    }

    highlightSerie() {
        this._group.selectAll('.highlighted')
            .classed('highlighted', false);
        if (this._serieState === 0) {
            this._hoverHighlightedSerie = true;
        }

        if (this._data && this._data.length > 0 && this._hoverHighlightedSerie) {
            this.setHighlightClass(true);
        }
    }

    private setHighlightClass(isHighlighted: boolean) {
        const selection = this._group.selectAll('.line');
        const pointSelection = this._group.selectAll('.scatter-series');
        const barSelection = this._group.selectAll('.bar');

        selection.classed('highlighted', isHighlighted);
        pointSelection.classed('highlighted', isHighlighted);
        barSelection.classed('highlighted', isHighlighted);
    }

    clearHighlightSerie() {
        if (this._serieState !== 2) {
            this._hoverHighlightedSerie = false;
        }
        if (!this._hoverHighlightedSerie) {
            this._group.selectAll('.highlighted').classed('highlighted', false);
        }
    }

    crosshairFormat(value?: (d: any) => string): (d: any) => string {
        if (arguments.length) {
            this._crosshairFormat = value;
        }
        return this._crosshairFormat;
    }

    legendAttributes(value?: any): any {
        if (arguments.length) {
            this._legendAttributes = value;
        }
        return this._legendAttributes;
    }

    private setHighlight(highlight: { values: any[]; classes: string[]; id: string; keyFunction }) {
        const current = highlight.values;
        const classed = highlight.classes[0];
        const secondaryClass = highlight.classes[1];
        const keyFunction = highlight.keyFunction;
        this._group
            .selectAll('.scatter-series')
            .data(current, d => keyFunction(d))
            .classed(classed, true)
            .classed(secondaryClass, true);
        this._group
            .selectAll(`.${classed}`)
            .data(current, d => keyFunction(d))
            .exit()
            .classed(classed, false);
    }

    private updateSerieState(state?: number) {
        if (state !== undefined) {
            this._serieState = state;
        } else {
            switch (this._serieState) {
                // Visible to Highlighted
                case 0:
                    this._serieState = 2;
                    break;
                // Highlighted to Hidden
                case 1:
                    this._serieState = 0;
                    break;
                // Hidden to Visible
                case 2:
                    this._serieState = 1;
                    break;
            }
        }
    }

    private redraw() {
        if (this._xScale && this._yScale) {
            this.resize(this._xScale, this._yScale);
        }
    }

    private _highlights: { values: any[], classes: string[], id: string, keyFunction }[] = [];
}